import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_PseudoElements = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🎨 CSS Pseudo-Elements</h1>

      <p className="tutorial-intro">
        A <b>pseudo-element</b> is used to style specific parts of an element.
        Unlike pseudo-classes (which target states), pseudo-elements let you
        style sub-parts like the first letter, first line, or insert content
        before/after elements.
      </p>

      {/* Highlight Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde047)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          🌟 Why Use Pseudo-Elements?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          They allow extra styling without adding new HTML elements. You can
          highlight the first letter, decorate text lines, or even add custom
          icons/content with <code>::before</code> and <code>::after</code>.
        </p>
      </div>
      {/* Common Uses of Pseudo-Elements */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "0.5rem" }}>
          📘 CSS Pseudo-Elements
        </h2>
        <p>
          A <b>CSS pseudo-element</b> is a keyword that can be added to a
          selector, to style a specific part of an element.
        </p>

        <h3 style={{ margin: "0.5rem 0", color: "#0369a1" }}>
          ✅ Some common uses:
        </h3>
        <ul style={{ lineHeight: "1.8", marginLeft: "1.2rem" }}>
          <li>
            Style the <b>first letter</b> or <b>first line</b> of an element
          </li>
          <li>
            Insert content <b>before</b> or <b>after</b> an element
          </li>
          <li>
            Style the <b>markers</b> of list items
          </li>
          <li>
            Style the <b>user-selected</b> portion of an element
          </li>
          <li>
            Style the <b>viewbox</b> behind a dialog box
          </li>
        </ul>
      </div>

      {/* 1. ::first-letter */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ ::first-letter</h2>
        <p>
          Styles the <b>first letter</b> of a block of text (like a drop cap in
          magazines).
        </p>

        <CodeCopyBlock
          code={`p::first-letter {
  font-size: 2rem;
  font-weight: bold;
  color: #dc2626;
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            padding: "10px",
            border: "1px solid #fca5a5",
            borderRadius: "6px",
          }}
        >
          <p style={{ margin: 0 }}>
            This is an example where the first letter will appear styled.
          </p>
        </div>
      </div>

      {/* 2. ::first-line */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ ::first-line</h2>
        <p>
          Styles the <b>first line</b> of a paragraph. Useful for decorative
          introductions.
        </p>

        <CodeCopyBlock
          code={`p::first-line {
  color: #2563eb;
  font-style: italic;
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            padding: "10px",
            border: "1px solid #93c5fd",
            borderRadius: "6px",
          }}
        >
          <p style={{ margin: 0 }}>
            This is an example text. The first line will appear styled with
            different color and font style for emphasis.
          </p>
        </div>
      </div>

      {/* 3. ::before */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ ::before</h2>
        <p>
          Inserts content <b>before</b> an element’s content. Commonly used for
          icons, quotes, or labels.
        </p>

        <CodeCopyBlock
          code={`h2::before {
  content: "👉 ";
  color: #16a34a;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <h2
            style={{
              border: "1px solid #86efac",
              padding: "5px",
              display: "inline-block",
            }}
          >
            Heading with ::before
          </h2>
        </div>
      </div>

      {/* 4. ::after */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ ::after</h2>
        <p>
          Inserts content <b>after</b> an element’s content. Often used for
          decorations or custom symbols.
        </p>

        <CodeCopyBlock
          code={`h2::after {
  content: " ✅";
  color: #16a34a;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <h2
            style={{
              border: "1px solid #bbf7d0",
              padding: "5px",
              display: "inline-block",
            }}
          >
            Heading with ::after
          </h2>
        </div>
      </div>

      {/* 5. ::selection */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ ::selection</h2>
        <p>
          Styles the part of text that the user <b>selects</b> with the mouse.
        </p>

        <CodeCopyBlock
          code={`p::selection {
  background: #facc15;
  color: black;
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            padding: "10px",
            border: "1px solid #fde68a",
            borderRadius: "6px",
          }}
        >
          <p style={{ margin: 0 }}>
            Try selecting this text with your mouse to see the styled
            <code>::selection</code>.
          </p>
        </div>
      </div>

      {/* Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #d1fae5, #bbf7d0)",
          padding: "1rem",
          borderRadius: "12px",
          margin: "2rem 0",
        }}
      >
        <h2 style={{ color: "#166534" }}>📌 Common Pseudo-Elements</h2>
        <ul style={{ lineHeight: "1.8" }}>
          <li>
            <b>::first-letter</b> → Style first letter
          </li>
          <li>
            <b>::first-line</b> → Style first line
          </li>
          <li>
            <b>::before</b> → Insert content before
          </li>
          <li>
            <b>::after</b> → Insert content after
          </li>
          <li>
            <b>::selection</b> → Style selected text
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          After pseudo-elements, try combining them with{" "}
          <a href="/css/pseudoclasses" className="learn-more">
            CSS Pseudo-Classes
          </a>{" "}
          for even more powerful styling tricks.
        </p>
      </div>
    </div>
  );
};

export default CSS_PseudoElements;
